import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import {
  FaUsers,
  FaGlobeAsia,
  FaRupeeSign,
  FaShieldAlt,
  FaHeadset,
  FaHandsHelping,
  FaChartLine,
} from "react-icons/fa";
import "./AboutUs.css"; // keep your existing CSS, but we'll override with inline styles

// ---------- SVG placeholder generator ----------
const svgPlaceholder = (
  width,
  height,
  text,
  bgColor = "#e9ecef",
  textColor = "#6c757d",
) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="${width}" height="${height}" fill="${bgColor}" />
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${Math.min(width, height) / 5}" fill="${textColor}" text-anchor="middle" dy=".3em">${text}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const AboutUs = () => {
  return (
    <React.Fragment>
      <Header />
      <div className="about-page">
        <Helmet>
          <title>
            About Us - Happy Pay Fintech: Empowering Retailers with Digital
            Financial Services
          </title>
          <meta
            name="description"
            content="Happy Pay Fintech provides a comprehensive platform for retailers and agents offering AEPS, DMT, CMS, PAN card services, and utility bill payments. Join us in building a digital India."
          />
        </Helmet>

        <div className="body-overlay"></div>

        <main>
          {/* ===== Hero / Breadcrumb (light gradient) ===== */}
          <section
            style={{
              background: "linear-gradient(145deg, #f9fafc 0%, #eef2f6 100%)",
              padding: "100px 0",
              textAlign: "center",
            }}
          >
            <div className="container">
              <div className="row">
                <div className="col-xxl-12">
                  <div className="breadcrumb__content p-relative z-index-1">
                    <h3
                      style={{
                        fontSize: "3rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "10px",
                        marginTop: "50px",
                      }}
                    >
                      About Happy Pay Fintech
                    </h3>
                    <div style={{ color: "#64748b", fontSize: "1.1rem" }}>
                      <a
                        href="/"
                        style={{ color: "#f39c12", textDecoration: "none" }}
                      >
                        Home
                      </a>
                      <span style={{ margin: "0 10px" }}>•</span>
                      <span>About Us</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Introduction ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#ffffff" }}>
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 mb-4 mb-lg-0">
                  <div className="intro-image text-center">
                    <img
                      src="/images/about-inner-1.jpg"
                      alt="Happy Pay Fintech Agent assisting a customer"
                      style={{
                        width: "100%",
                        maxWidth: "500px",
                        borderRadius: "20px",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.05)",
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="intro-content">
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: "#f39c1220",
                        color: "#f39c12",
                        fontWeight: "600",
                        fontSize: "0.9rem",
                        padding: "4px 16px",
                        borderRadius: "30px",
                        marginBottom: "16px",
                      }}
                    >
                      Welcome to Happy Pay Fintech
                    </span>
                    <h2
                      style={{
                        fontSize: "clamp(2rem, 4vw, 2.8rem)",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "20px",
                      }}
                    >
                      Your Trusted Partner for Digital Financial Solutions
                    </h2>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        color: "#64748b",
                        lineHeight: "1.6",
                      }}
                    >
                      Founded in 2025, Happy Pay Fintech has rapidly grown to
                      become a leading fintech service provider for retailers
                      and agents across India. We believe in empowering local
                      businesses with cutting‑edge technology, enabling them to
                      offer a wide range of financial services – from AEPS and
                      money transfer to bill payments and PAN card issuance –
                      all from a single, easy‑to‑use platform.
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        color: "#64748b",
                        lineHeight: "1.6",
                        marginTop: "20px",
                      }}
                    >
                      Our mission is to bridge the gap between traditional
                      banking and the unbanked, ensuring that every Indian, no
                      matter how remote, has access to essential financial
                      services. With over 20,000 active retailers and 10 million
                      monthly transactions, we are proud to be part of India’s
                      digital revolution.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Services ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#f9fafc" }}>
            <div className="container">
              <div style={{ textAlign: "center", marginBottom: "60px" }}>
                <span
                  style={{
                    display: "inline-block",
                    backgroundColor: "#f39c1220",
                    color: "#f39c12",
                    fontWeight: "600",
                    fontSize: "0.9rem",
                    padding: "4px 16px",
                    borderRadius: "30px",
                    marginBottom: "16px",
                  }}
                >
                  What We Offer
                </span>
                <h2
                  style={{
                    fontSize: "clamp(2rem, 4vw, 2.8rem)",
                    fontWeight: "700",
                    color: "#1e293b",
                    marginBottom: "20px",
                  }}
                >
                  Financial Services at Your Fingertips
                </h2>
              </div>
              <div className="row g-4 justify-content-center">
                {[
                  {
                    img: "fingerprint-icon.png",
                    title: "AEPS",
                    desc: "Cash withdrawals, balance inquiries with Aadhaar authentication.",
                  },
                  {
                    img: "bank-icon.png",
                    title: "DMT",
                    desc: "Instant, secure money transfer to any bank account in India.",
                  },
                  {
                    img: "rupee-icon.png",
                    title: "CMS",
                    desc: "Cash management services for businesses.",
                  },
                  {
                    img: "pancard-icon.png",
                    title: "PAN Card",
                    desc: "Instant PAN card issuance for your customers.",
                  },
                  {
                    img: "bill-icon.png",
                    title: "Utility Bill Payments",
                    desc: "Electricity, water, gas, DTH, mobile recharge – all in one dashboard.",
                  },
                ].map((service, idx) => (
                  <div className="col-lg-4 col-md-6" key={idx}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "16px",
                        padding: "30px 20px",
                        textAlign: "center",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                        border: "1px solid #eef2f6",
                        height: "100%",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow =
                          "0 20px 40px rgba(243,156,18,0.1)";
                        e.currentTarget.style.borderColor = "#f39c1240";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 10px 30px rgba(0,0,0,0.03)";
                        e.currentTarget.style.borderColor = "#eef2f6";
                      }}
                    >
                      <div
                        style={{
                          width: "70px",
                          height: "70px",
                          margin: "0 auto 20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#ffffff",
                          borderRadius: "50%",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                          color: "#f39c12",
                          fontSize: "2rem",
                        }}
                      >
                        <img
                          src={`/images/${service.img}`}
                          alt={service.title}
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      <h5
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "600",
                          color: "#1e293b",
                          marginBottom: "12px",
                        }}
                      >
                        {service.title}
                      </h5>
                      <p
                        style={{
                          fontSize: "0.95rem",
                          color: "#64748b",
                          lineHeight: "1.6",
                          marginBottom: 0,
                        }}
                      >
                        {service.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Mission & Vision ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#ffffff" }}>
            <div className="container">
              <div className="row g-4">
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "16px",
                      padding: "30px",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        marginBottom: "20px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "2px solid #f39c12",
                      }}
                    >
                      <img
                        src="/images/mission.png"
                        alt="Our Mission"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <h4
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "15px",
                      }}
                    >
                      Our Mission
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#64748b",
                        lineHeight: "1.7",
                      }}
                    >
                      To empower every retailer and agent in India with a
                      simple, reliable, and profitable platform to offer
                      essential digital financial services, bridging the gap
                      between traditional banking and the unbanked.
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "16px",
                      padding: "30px",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        marginBottom: "20px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "2px solid #f39c12",
                      }}
                    >
                      <img
                        src="/images/vision.png"
                        alt="Our Vision"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <h4
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "15px",
                      }}
                    >
                      Our Vision
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#64748b",
                        lineHeight: "1.7",
                      }}
                    >
                      To be the most trusted and widely adopted fintech partner
                      for small businesses and agents, driving financial
                      inclusion and becoming synonymous with 'digital payments'
                      in every local community.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Stats ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#f9fafc" }}>
            <div className="container">
              <div className="row g-4">
                <div className="col-md-4">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "16px",
                      padding: "40px 20px",
                      textAlign: "center",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <div
                      style={{
                        width: "70px",
                        height: "70px",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f39c1220",
                        borderRadius: "50%",
                        color: "#f39c12",
                        fontSize: "2rem",
                      }}
                    >
                      <FaUsers size={40} />
                    </div>
                    <h3
                      style={{
                        fontSize: "2.2rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "5px",
                      }}
                    >
                      20000+
                    </h3>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#64748b",
                        marginBottom: 0,
                      }}
                    >
                      Active Retailers
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "16px",
                      padding: "40px 20px",
                      textAlign: "center",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <div
                      style={{
                        width: "70px",
                        height: "70px",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f39c1220",
                        borderRadius: "50%",
                        color: "#f39c12",
                        fontSize: "2rem",
                      }}
                    >
                      <FaRupeeSign size={40} />
                    </div>
                    <h3
                      style={{
                        fontSize: "2.2rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "5px",
                      }}
                    >
                      10M+
                    </h3>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#64748b",
                        marginBottom: 0,
                      }}
                    >
                      Monthly Transactions
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "16px",
                      padding: "40px 20px",
                      textAlign: "center",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <div
                      style={{
                        width: "70px",
                        height: "70px",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f39c1220",
                        borderRadius: "50%",
                        color: "#f39c12",
                        fontSize: "2rem",
                      }}
                    >
                      <FaGlobeAsia size={40} />
                    </div>
                    <h3
                      style={{
                        fontSize: "2.2rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "5px",
                      }}
                    >
                      50+
                    </h3>
                    <p
                      style={{
                        fontSize: "1rem",
                        color: "#64748b",
                        marginBottom: 0,
                      }}
                    >
                      Partners & Service Providers
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Why Partner With Us ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#ffffff" }}>
            <div className="container">
              <h2
                style={{
                  fontSize: "clamp(2rem, 4vw, 2.8rem)",
                  fontWeight: "700",
                  color: "#1e293b",
                  textAlign: "center",
                  marginBottom: "50px",
                }}
              >
                Why Partner with Happy Pay?
              </h2>
              <div className="row g-4">
                {[
                  {
                    icon: <FaShieldAlt size={40} />,
                    title: "Secure & Reliable",
                    desc: "Bank-grade security for every transaction.",
                  },
                  {
                    icon: <FaHeadset size={40} />,
                    title: "Dedicated Support",
                    desc: "24/7 assistance for all your queries.",
                  },
                  {
                    icon: <FaGlobeAsia size={40} />,
                    title: "Pan-India Presence",
                    desc: "Empowering agents from metro to rural areas.",
                  },
                ].map((item, idx) => (
                  <div className="col-md-4" key={idx}>
                    <div
                      style={{
                        backgroundColor: "#f9fafc",
                        borderRadius: "16px",
                        padding: "30px 20px",
                        textAlign: "center",
                        border: "1px solid #eef2f6",
                        height: "100%",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow =
                          "0 20px 40px rgba(243,156,18,0.1)";
                        e.currentTarget.style.borderColor = "#f39c1240";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.borderColor = "#eef2f6";
                      }}
                    >
                      <div
                        style={{
                          width: "70px",
                          height: "70px",
                          margin: "0 auto 20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#ffffff",
                          borderRadius: "50%",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                          color: "#f39c12",
                        }}
                      >
                        {item.icon}
                      </div>
                      <h5
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "600",
                          color: "#1e293b",
                          marginBottom: "10px",
                        }}
                      >
                        {item.title}
                      </h5>
                      <p
                        style={{
                          fontSize: "0.95rem",
                          color: "#64748b",
                          marginBottom: 0,
                        }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Impact Numbers ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#f9fafc" }}>
            <div className="container">
              <div className="row g-4">
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "20px",
                      padding: "40px 30px",
                      textAlign: "center",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "2.2rem",
                        fontWeight: "700",
                        color: "#f39c12",
                        marginBottom: "10px",
                      }}
                    >
                      1+ Million
                    </h4>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        color: "#64748b",
                        marginBottom: "20px",
                      }}
                    >
                      Transactions Processed
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: "#f39c1220",
                          color: "#f39c12",
                          padding: "6px 16px",
                          borderRadius: "30px",
                          fontSize: "0.9rem",
                          fontWeight: "500",
                        }}
                      >
                        📌 10,000+ Pincodes
                      </span>
                      <span
                        style={{
                          backgroundColor: "#f39c1220",
                          color: "#f39c12",
                          padding: "6px 16px",
                          borderRadius: "30px",
                          fontSize: "0.9rem",
                          fontWeight: "500",
                        }}
                      >
                        📌 100% Digital
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "20px",
                      padding: "40px 30px",
                      textAlign: "center",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "2.2rem",
                        fontWeight: "700",
                        color: "#f39c12",
                        marginBottom: "10px",
                      }}
                    >
                      ₹500+ Crore
                    </h4>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        color: "#64748b",
                        marginBottom: "20px",
                      }}
                    >
                      Total Value Processed
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: "#f39c1220",
                          color: "#f39c12",
                          padding: "6px 16px",
                          borderRadius: "30px",
                          fontSize: "0.9rem",
                          fontWeight: "500",
                        }}
                      >
                        💰 Commissions Paid
                      </span>
                      <span
                        style={{
                          backgroundColor: "#f39c1220",
                          color: "#f39c12",
                          padding: "6px 16px",
                          borderRadius: "30px",
                          fontSize: "0.9rem",
                          fontWeight: "500",
                        }}
                      >
                        💰 ₹1+ Crore
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Leadership Team ===== */}
          <section style={{ padding: "80px 0", backgroundColor: "#ffffff" }}>
            <div className="container">
              <h2
                style={{
                  fontSize: "clamp(2rem, 4vw, 2.8rem)",
                  fontWeight: "700",
                  color: "#1e293b",
                  textAlign: "center",
                  marginBottom: "15px",
                }}
              >
                Meet Our Leadership Team
              </h2>
              <p
                style={{
                  fontSize: "1.1rem",
                  color: "#64748b",
                  textAlign: "center",
                  maxWidth: "600px",
                  margin: "0 auto 50px",
                }}
              >
                Experienced professionals dedicated to your success.
              </p>
              <div className="row g-4">
                {[
                  { name: "Anand S", role: "Founder & CEO" },
                  { name: "Rohan Mehta", role: "Chief Technology Officer" },
                  { name: "Priya Singh", role: "Head of Operations" },
                  { name: "Vikram Joshi", role: "Business Development" },
                ].map((member, idx) => (
                  <div className="col-lg-3 col-md-6" key={idx}>
                    <div
                      style={{
                        backgroundColor: "#f9fafc",
                        borderRadius: "16px",
                        padding: "30px 20px 25px",
                        textAlign: "center",
                        border: "1px solid #eef2f6",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow =
                          "0 20px 40px rgba(243,156,18,0.1)";
                        e.currentTarget.style.borderColor = "#f39c1240";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.borderColor = "#eef2f6";
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: "150px",
                          height: "150px",
                          margin: "0 auto 20px",
                        }}
                      >
                        <img
                          src={svgPlaceholder(
                            150,
                            150,
                            member.name.split(" ")[0],
                          )}
                          alt={member.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "3px solid #f39c12",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            right: "0",
                            background:
                              "linear-gradient(to top, rgba(243,156,18,0.9), transparent)",
                            padding: "20px 0 10px",
                            borderRadius: "0 0 50% 50%",
                            display: "flex",
                            justifyContent: "center",
                            gap: "10px",
                            opacity: 0,
                            transition: "opacity 0.3s ease",
                          }}
                          className="team-social-hover"
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = 1)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = 0)
                          }
                        >
                          <a
                            href="#"
                            style={{ color: "#fff", fontSize: "1rem" }}
                          >
                            <i className="fa-brands fa-facebook-f"></i>
                          </a>
                          <a
                            href="#"
                            style={{ color: "#fff", fontSize: "1rem" }}
                          >
                            <i className="fa-brands fa-twitter"></i>
                          </a>
                          <a
                            href="#"
                            style={{ color: "#fff", fontSize: "1rem" }}
                          >
                            <i className="fa-brands fa-linkedin-in"></i>
                          </a>
                        </div>
                      </div>
                      <h5
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "600",
                          color: "#1e293b",
                          marginBottom: "5px",
                        }}
                      >
                        {member.name}
                      </h5>
                      <p
                        style={{
                          fontSize: "0.95rem",
                          color: "#64748b",
                          marginBottom: 0,
                        }}
                      >
                        {member.role}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default AboutUs;
